import ReactGA from 'react-ga4';
////////////////////////////////////////////////////////////////////
import {
	BackButton,
	Button,
	PageTitle,
	SkinCarousel,
} from '@/app/shared/components';
import { useEffect } from 'react';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { useParams } from 'react-router-dom';
import {
	setActivePage,
	setCounter,
	setDeselectSkin,
	setDiamonds,
	setLevel,
	setLevelRequeriments,
	setPurchasedSkin,
	setSelectedSkin,
	setStickerWonDialog,
} from '@/core/redux/slices';
import { toast } from 'sonner';
import { SkinsService } from '@/core/services';
import { useTranslation } from 'react-i18next';
import { BuySkin, SelectSkin } from '@/core/interfaces';

const SkinPack: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const { skinPackId } = useParams();
	const { t } = useTranslation();
	const dispatch = useCustomDispatch();

	/*********************
	// #region Redux
	**********************/
	const { active: activeSkin } = useCustomSelector((state) => state.skins);
	const { diamonds, level: currentLevel } = useCustomSelector(
		(state) => state.counter
	);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		if (activeSkin) return;
		if (!skinPackId) return;
		dispatch(setActivePage(skinPackId));
	}, [skinPackId]);

	useEffect(() => {
		if (!activeSkin) return;
		ReactGA.send({
			hitType: 'pageview',
			page: `/skins/${activeSkin.id})}`,
			title: `Page skin ${activeSkin.name}`,
		});
	}, [activeSkin]);

	const handleBuySkin = () => {
		if (!activeSkin) return;

		if (activeSkin.price > diamonds) {
			toast.warning(t('general.notEnoughGems'));
			return;
		}

		toast.promise(SkinsService.buySkin(activeSkin.id), {
			loading: t('skins.buy.processing'),
			success: (data: BuySkin) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'shop',
					action: `Buy ${activeSkin.name}`,
				});

				// Update skin purchased
				dispatch(setPurchasedSkin(data.skin));
				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));

				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return `${t('skins.buy.success')}`;
			},
			error: (e: any) => {
				console.log(e);
				return `${t('skins.buy.error')}`;
			},
		});
	};

	const handleActivateSkin = () => {
		if (!activeSkin) return;

		toast.promise(SkinsService.activateSkin(activeSkin.id), {
			loading: t('skins.activate.processing'),
			success: (data: SelectSkin) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'skins',
					action: `Activate ${activeSkin.name}`,
				});

				// Update skin selected
				dispatch(setSelectedSkin(activeSkin));
				// Update levels images
				dispatch(setLevelRequeriments(data.levels));

				dispatch(setLevel(data.levels[currentLevel - 1].level));
				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));

				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return `${t('skins.activate.success')}`;
			},
			error: `${t('skins.activate.error')}`,
		});
	};

	const handleDeactivateSkin = () => {
		if (!activeSkin) return;

		toast.promise(SkinsService.deactivateSkin('default'), {
			loading: t('skins.deactivate.processing'),
			success: (data: SelectSkin) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'shop',
					action: `Deactivate ${activeSkin.name}`,
				});

				// Update skin selected
				dispatch(setDeselectSkin());
				// Update levels images
				dispatch(setLevelRequeriments(data.levels));

				dispatch(setLevel(data.levels[currentLevel - 1].level));
				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));

				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return `${t('skins.deactivate.success')}`;
			},
			error: `${t('skins.deactivate.error')}`,
		});
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-2 pt-8 pb-32 mx-4">
			<BackButton />

			<div
				className="relative group rounded-2xl bg-cover bg-center transition-all duration-300 shadow-lg shadow-pink-600 mt-4 py-8 bg-black/500"
				style={{ backgroundImage: `url(${activeSkin?.cover})` }}
			>
				<div className="absolute inset-0 bg-black/70 rounded-2xl"></div>

				<div className="relative flex flex-col items-center z-10">
					<PageTitle>{activeSkin?.name}</PageTitle>
				</div>
			</div>

			{activeSkin &&
				activeSkin.images &&
				activeSkin.images.length > 0 && (
					<SkinCarousel skin={activeSkin} />
				)}

			<div className="mt-8">
				{activeSkin && activeSkin.owned && !activeSkin.selected && (
					<Button variant="secondary" onClick={handleActivateSkin}>
						Activate skin
					</Button>
				)}

				{activeSkin && activeSkin.owned && activeSkin.selected && (
					<Button variant="primary" onClick={handleDeactivateSkin}>
						Desactivate skin
					</Button>
				)}

				{activeSkin && !activeSkin.owned && !activeSkin.selected && (
					<Button
						variant="secondary"
						className="flex items-center gap-1"
						onClick={handleBuySkin}
					>
						<p className="leading-none">Buy skin</p>

						<div className="flex gap-1 items-center">
							<span className="font-bold leading-none">
								{activeSkin.price}
							</span>

							<img
								src={process.env.VITE_MINI_DIAMOND_IMAGE}
								alt="Buy skin"
								className="w-[13px] h-[13px]"
							/>
						</div>
					</Button>
				)}
			</div>
		</div>
	);
};

export default SkinPack;
